import { useLayoutEffect, useRef } from 'react';

import { useKeycloak } from '@hooks/useKeycloak';

import './ImpersonationBanner.scss';

export const ImpersonationBanner = () => {
  const { keycloak } = useKeycloak();
  const tokenParsed = keycloak?.tokenParsed;
  const impersonatorUsername = tokenParsed?.impersonator?.username;
  const impersonatedUserName = tokenParsed?.name || tokenParsed?.preferred_username || tokenParsed?.email;
  const isImpersonating = !!impersonatorUsername;

  const bannerRef = useRef<HTMLDivElement>(null);

  // Expose the banner's live height so the fixed header and page content can be pushed down by it.
  useLayoutEffect(() => {
    if (!isImpersonating) return;

    const banner = bannerRef.current;
    document.body.classList.add('impersonation-active');

    const setHeight = () => {
      document.body.style.setProperty('--impersonation-banner-height', `${banner?.offsetHeight ?? 0}px`);
    };
    setHeight();

    const observer = new ResizeObserver(setHeight);
    if (banner) observer.observe(banner);

    return () => {
      observer.disconnect();
      document.body.classList.remove('impersonation-active');
      document.body.style.removeProperty('--impersonation-banner-height');
    };
  }, [isImpersonating, impersonatedUserName, impersonatorUsername]);

  if (!isImpersonating) return null;

  return (
    <div
      ref={bannerRef}
      role='status'
      data-testid='impersonation-banner'
      className='impersonation-banner text-bg-warning position-fixed top-0 start-0 end-0 d-flex flex-wrap align-items-center justify-content-center gap-2 px-3 py-2'
    >
      <span className='badge text-bg-dark text-uppercase'>Impersonation session</span>
      <span>
        Viewing as <strong>{impersonatedUserName ?? 'this user'}</strong>
        {impersonatorUsername && (
          <>
            {' '}
            - impersonated by <strong>{impersonatorUsername}</strong>
          </>
        )}
      </span>
    </div>
  );
};
